import { Component, HostBinding, ViewChild } from '@angular/core';
import { RowNode } from 'ag-grid-community';
import { ContextMenuOption } from '../../../../models/context-menu-option';
import { DrRequestInfo } from '../../../../models/dr-request-info';
import { Endpoint, HelpIndex, PatternOperationType, UpdateStatusType, SettingsType } from '../../../../models/enums';
import { AlignDirection, CellTemplateType, GenericColDef, GridContext } from '../../../../models/grid-context';
import { Pattern } from '../../../../models/pattern';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { Template } from '../../../../models/template';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { GenericGridComponent } from '../../../generic-grid/generic-grid.component';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { DeleteEntityModalComponent } from '../../delete-entity-modal/delete-entity-modal.component';
import { PatternModalComponent } from '../../pattern-modal/pattern-modal.component';
import { ObjectActionListType, Permissions, ActionType, PermissionSet } from '../../../../models/permission';

@Component({
    standalone: false,
    selector: 'res-patterns',
    templateUrl: './patterns.component.html',
    styleUrls: ['./patterns.component.scss']
})
export class PatternsComponent implements SettingsNavigationContentComponent {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild(GenericGridComponent, { static: true }) genericGrid: GenericGridComponent;

    isDirty: boolean;
    validChanges = true;
    pattern: Pattern;
    updateStatusType = UpdateStatusType.PATTERN;

    gridName = 'Patterns';
    automationId = 'patterns';
    helpIndex: any;

    isBusy = false;
    isBusyMessage = '';

    currentHandle = '';

    // permissions
    permissionType: ObjectActionListType;
    hasPatternsViewOnlyPermissions: boolean;
    isSystemUser = false;
    hasContextMenuPermissions = true;

    constructor(private commonService: CommonService,
        private restService: RestService) {
    }

    columnDefs = new Array<GenericColDef>(
        {
            headerName: 'Name',
            field: 'name',
            sortBy: 'name',
            automationId: this.automationId,
            lockVisible: true
        },
        {
            headerName: 'Searchable Token Name',
            automationId: this.automationId,
            hide: true,
            valueGetter: function tokenNameGetter(params) {
                return `'` + 'token-' + params.data.name.toLowerCase() + `'`;
            }
        },
        {
            headerName: 'Description',
            field: 'description',
            sortBy: 'description',
            automationId: this.automationId
        },
        {
            headerName: 'Enable',
            field: 'enabled',
            sortBy: 'enabled',
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.iconSwitch,
            cellRendererParams: { iconOnClass: 'fa-check color-grey-10' },
            headerAlignDirection: AlignDirection.center,
            width: 70,
            suppressSizeToFit: true
        },
        {
            headerName: 'Store Value',
            field: 'storeValue',
            sortBy: 'storeValue',
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.iconSwitch,
            cellRendererParams: { iconOnClass: 'fa-check color-grey-10' },
            headerAlignDirection: AlignDirection.center,
            width: 90,
            suppressSizeToFit: true
        },
        {
            headerName: 'Pattern',
            field: 'expression',
            automationId: this.automationId,
            sortBy: 'expression',
            width: 95
        },
        {
            headerName: 'Created By',
            field: 'createdBy',
            sortBy: 'createdBy',
            automationId: this.automationId,
            width: 95
        },
        {
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.contextMenu,
            minWidth: 30, maxWidth: 30, width: 30,
            suppressSizeToFit: true
        },
    );

    requestInfo = new DrRequestInfo({
        endpoint: Endpoint.projectManager_listRegexes,
        responseName: 'regexes'
    });
    gridContext = new GridContext({
        sortBy: 'name',
        sortAscending: true
    });

    rowClassRules = {
        'disabled-row': () => {
            return this.hasPatternsViewOnlyPermissions;
        }
    };

    contextMenuOptions = [
        new ContextMenuOption({
            name: 'Edit ...', iconClass: 'fa-pencil', iconColor: 'color-blue-01', automationId: this.automationId,
            click: (item) => this.editPattern(item)
        }),
        new ContextMenuOption({
            name: 'Copy ...', iconClass: 'fa-copy', iconColor: 'color-grey-10', automationId: this.automationId,
            click: (item) => this.copyPattern(item)
        }),
        new ContextMenuOption({
            name: 'Delete', iconClass: 'fa-trash', iconColor: 'color-red-05', automationId: this.automationId,
            click: (item) => this.deletePattern(item), enabled: (item) => (item.createdBy !== 'System')
        })
    ];

    editPattern(item: Pattern): void {
        this.commonService.openModal(PatternModalComponent,
            {
                backdrop: false,
                modalData: {
                    patternOperationType: item.createdBy === 'System' ? PatternOperationType.edit_system_pattern : PatternOperationType.edit_custom_pattern,
                    data: item,
                    currentHandle: this.currentHandle,
                    systemScope: this.isSystemUser
                }
            }).then((result) => {
                if (result.modalResult) {
                    this.genericGrid.loadData();
                }
            });
    }

    copyPattern(item: Pattern): void {
        this.commonService.openModal(PatternModalComponent,
            { backdrop: false, modalData: { patternOperationType: PatternOperationType.copy_pattern, data: item, currentHandle: this.currentHandle, systemScope: this.isSystemUser } }).then((result) => {
                if (result.modalResult) {
                    this.genericGrid.loadData();
                }
            });
    }

    addNewPattern(): void {
        this.commonService.openModal(PatternModalComponent,
            { backdrop: false, modalData: { patternOperationType: PatternOperationType.new_pattern, currentHandle: this.currentHandle, systemScope: this.isSystemUser } }).then((result) => {
                if (result.modalResult) {
                    this.genericGrid.loadData();
                }
            });
    }

    deletePattern(item: Pattern): void {
        Object.assign(item, { systemScope: this.isSystemUser });
        this.commonService.openModal(DeleteEntityModalComponent, { modalData: item });
    }

    responseArrayCallback = (value: any) => new Pattern(value);

    cellDoubleClickedCallback = (rowNode: RowNode) => {
        if (rowNode && rowNode.data instanceof Pattern) {
            this.editPattern(rowNode.data);
        }
    }

    saveChanges(): Promise<boolean> { return Promise.resolve(true); }

    selectedTreeItemChanged(node: GenericTreeNode, projectHandle?: string, settingsType?: SettingsType): void {
        this.currentHandle = node.data instanceof Template ? node.data.handle : projectHandle;
        this.helpIndex = projectHandle ? HelpIndex.case_patterns_dlg : (settingsType === SettingsType.system ? HelpIndex.sys_template_patterns_dlg : HelpIndex.org_patterns_template_dlg);
        this.isSystemUser = (settingsType === SettingsType.system && (this.commonService.currentUser?.originalCustomerName === 'super_system_customer' || this.commonService.currentUser.importedFromSystemUser));
        this.permissionType = projectHandle ? ObjectActionListType.PRJ_REGULAR_EXPRESSIONS : this.isSystemUser ? ObjectActionListType.SYSTEM_PATTERNS : ObjectActionListType.ORG_REGULAR_EXPRESSIONS;

        this.hasPatternsViewOnlyPermissions = !Permissions.checkUserPermission(this.permissionType, ActionType.CREATE);

        const deleteAction = projectHandle ? ActionType.DELETE : ActionType.CREATE;

        this.contextMenuOptions[2].permissions = [new PermissionSet({ permissionType: this.permissionType, permissionActions: [deleteAction] })];

        if (this.columnDefs.find(c => c.cellTemplateType === CellTemplateType.contextMenu)) {
            if (this.hasPatternsViewOnlyPermissions) {
                this.columnDefs = this.columnDefs.filter(col => col.cellTemplateType !== CellTemplateType.contextMenu);
                this.hasContextMenuPermissions = false;
            }
        }

        if (this.currentHandle) {
            this.requestInfo.data = {
                projectHandle: this.currentHandle
            };
            this.genericGrid.loadData();
        }
        if (this.isSystemUser) {
            Object.assign(this.requestInfo.data, { systemScope: true });
        }
    }
}
